// React
import {
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
} from "react";
// Navigation
import { useNavigate, useParams } from "react-router-dom";
// FHIR
import { Appointment, Reference } from "fhir/r4";
// Translation
import i18n from "i18next";
// HL7 front library
import { StatusTag, Title } from "@fyrstain/hl7-front-library";
// Component
import AcesoPage from "src/components/AcesoPage/AcesoPage";
// Services
import {
  formatDate,
  formatPeriod,
  getCodeableConceptValue,
  getCodeableConceptValues,
  getIdentifierValues,
  getReferenceIdentifier,
  getReferencePath,
  getReferenceValue,
  getStatusFlavor,
} from "src/services/resourceDetails.utils";
// Style
import "./AppointmentDetailsPage.css";

const AppointmentDetailsPage: FunctionComponent = () => {
  const { resourceId } = useParams();
  const navigate = useNavigate();

  const [appointment, setAppointment] =
    useState<Appointment>();
  const [loading, setLoading] = useState(true);

  //////////////////////////////
  //           Error          //
  //////////////////////////////

  const onError = useCallback((error?: unknown) => {
    console.error(
      "Appointment details error:",
      error ?? "Network or FHIR error",
    );
  }, []);

  //////////////////////////////
  //          Request         //
  //////////////////////////////

  useEffect(() => {
    if (!resourceId) {
      setLoading(false);
      return;
    }

    const controller = new AbortController();

    fetch(
      `${process.env.REACT_APP_FHIR_URL}/Appointment/${resourceId}`,
      {
        signal: controller.signal,
      },
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            `FHIR request failed with status ${response.status}`,
          );
        }

        return response.json() as Promise<Appointment>;
      })
      .then((data) => {
        setAppointment(data);
      })
      .catch((error: unknown) => {
        if (
          error instanceof DOMException &&
          error.name === "AbortError"
        ) {
          return;
        }

        onError(error);
      })
      .finally(() => {
        setLoading(false);
      });

    return () => {
      controller.abort();
    };
  }, [onError, resourceId]);

  //////////////////////////////
  //         Functions        //
  //////////////////////////////

  function getPatient(): Reference | undefined {
    return appointment?.participant?.find(
      (participant) =>
        participant.actor?.type === "Patient" ||
        participant.actor?.reference?.startsWith(
          "Patient/",
        ),
    )?.actor;
  }

  function renderStringList(values: string[]) {
    return (
      <ul className="resource-details-list">
        {values.map((value, index) => (
          <li key={`${value}-${index}`}>
            {value}
          </li>
        ))}
      </ul>
    );
  }

  function renderReferences(
    references?: Reference[],
  ) {
    if (!references?.length) {
      return (
        <span className="resource-details-value">
          {i18n.t("general.notavailable")}
        </span>
      );
    }

    return renderStringList(
      references.map((reference) =>
        getReferenceValue(reference),
      ),
    );
  }

  //////////////////////////////
  //          Content         //
  //////////////////////////////

  const patient = getPatient();

  return (
    <AcesoPage
      loading={loading}
      titleKey="pathway.tabs.appointments"
      needsLogin={true}
    >
      {!appointment ? (
        <div className="alert alert-info">
          {i18n.t("general.notavailable")}
        </div>
      ) : (
        <div className="resource-details-page">
          <div className="resource-details-toolbar">
            <button
              className="resource-details-back-button"
              onClick={() => navigate(-1)}
              type="button"
            >
              {i18n.t("button.backtolist")}
            </button>
          </div>

          <section className="resource-details-section">
            <Title
              level={2}
              content={i18n.t(
                "details.sections.patient",
              )}
            />

            <div className="row g-3">
              <div className="col-md-6">
                <div className="resource-details-card">
                  <span className="resource-details-label">
                    {i18n.t("label.patient")}
                  </span>

                  <span className="resource-details-value">
                    {getReferenceValue(patient)}
                  </span>
                </div>
              </div>

              <div className="col-md-6">
                <div className="resource-details-card">
                  <span className="resource-details-label">
                    {i18n.t(
                      "label.patientidentifier",
                    )}
                  </span>

                  <span className="resource-details-value">
                    {getReferenceIdentifier(patient)}
                  </span>
                </div>
              </div>

              <div className="col-12">
                <div className="resource-details-card">
                  <span className="resource-details-label">
                    {i18n.t(
                      "label.patientreference",
                    )}
                  </span>

                  <span className="resource-details-value">
                    {getReferencePath(patient)}
                  </span>
                </div>
              </div>
            </div>
          </section>

          <section className="resource-details-section">
            <Title
              level={2}
              content={i18n.t(
                "details.sections.appointment",
              )}
            />

            <div className="row g-3">
              <div className="col-md-6">
                <div className="resource-details-card">
                  <span className="resource-details-label">
                    {i18n.t("label.status")}
                  </span>

                  <StatusTag
                    message={i18n.t(
                      `appointment.status.${appointment.status}`,
                      {
                        defaultValue:
                          appointment.status,
                      },
                    )}
                    flavor={getStatusFlavor(
                      appointment.status,
                    )}
                  />
                </div>
              </div>

              <div className="col-md-6">
                <div className="resource-details-card">
                  <span className="resource-details-label">
                    {i18n.t("label.priority")}
                  </span>

                  <span className="resource-details-value">
                    {appointment.priority ??
                      i18n.t(
                        "general.notavailable",
                      )}
                  </span>
                </div>
              </div>

              <div className="col-12">
                <div className="resource-details-card">
                  <span className="resource-details-label">
                    {i18n.t(
                      "label.appointmentidentifiers",
                    )}
                  </span>

                  {renderStringList(
                    getIdentifierValues(
                      appointment.identifier,
                    ),
                  )}
                </div>
              </div>

              <div className="col-md-6">
                <div className="resource-details-card">
                  <span className="resource-details-label">
                    {i18n.t("label.startdate")}
                  </span>

                  <span className="resource-details-value">
                    {formatDate(appointment.start)}
                  </span>
                </div>
              </div>

              <div className="col-md-6">
                <div className="resource-details-card">
                  <span className="resource-details-label">
                    {i18n.t("label.enddate")}
                  </span>

                  <span className="resource-details-value">
                    {formatDate(appointment.end)}
                  </span>
                </div>
              </div>

              <div className="col-md-6">
                <div className="resource-details-card">
                  <span className="resource-details-label">
                    {i18n.t("label.duration")}
                  </span>

                  <span className="resource-details-value">
                    {appointment.minutesDuration !==
                    undefined
                      ? `${appointment.minutesDuration} min`
                      : i18n.t(
                          "general.notavailable",
                        )}
                  </span>
                </div>
              </div>

              <div className="col-md-6">
                <div className="resource-details-card">
                  <span className="resource-details-label">
                    {i18n.t("label.creationdate")}
                  </span>

                  <span className="resource-details-value">
                    {formatDate(appointment.created)}
                  </span>
                </div>
              </div>

              <div className="col-12">
                <div className="resource-details-card">
                  <span className="resource-details-label">
                    {i18n.t("label.description")}
                  </span>

                  <span className="resource-details-value">
                    {appointment.description ??
                      i18n.t(
                        "general.notavailable",
                      )}
                  </span>
                </div>
              </div>

              <div className="col-12">
                <div className="resource-details-card">
                  <span className="resource-details-label">
                    {i18n.t("label.comment")}
                  </span>

                  <span className="resource-details-value">
                    {appointment.comment ??
                      i18n.t(
                        "general.notavailable",
                      )}
                  </span>
                </div>
              </div>

              <div className="col-12">
                <div className="resource-details-card">
                  <span className="resource-details-label">
                    {i18n.t(
                      "label.patientinstruction",
                    )}
                  </span>

                  <span className="resource-details-value">
                    {appointment.patientInstruction ??
                      i18n.t(
                        "general.notavailable",
                      )}
                  </span>
                </div>
              </div>
            </div>
          </section>

          <section className="resource-details-section">
            <Title
              level={2}
              content={i18n.t(
                "details.sections.classification",
              )}
            />

            <div className="row g-3">
              <div className="col-md-6">
                <div className="resource-details-card resource-details-static-card">
                  <span className="resource-details-label">
                    {i18n.t(
                      "label.servicecategory",
                    )}
                  </span>

                  {renderStringList(
                    getCodeableConceptValues(
                      appointment.serviceCategory,
                    ),
                  )}
                </div>
              </div>

              <div className="col-md-6">
                <div className="resource-details-card resource-details-static-card">
                  <span className="resource-details-label">
                    {i18n.t("label.servicetype")}
                  </span>

                  {renderStringList(
                    getCodeableConceptValues(
                      appointment.serviceType,
                    ),
                  )}
                </div>
              </div>

              <div className="col-md-6">
                <div className="resource-details-card resource-details-static-card">
                  <span className="resource-details-label">
                    {i18n.t("label.specialty")}
                  </span>

                  {renderStringList(
                    getCodeableConceptValues(
                      appointment.specialty,
                    ),
                  )}
                </div>
              </div>

              <div className="col-md-6">
                <div className="resource-details-card resource-details-static-card">
                  <span className="resource-details-label">
                    {i18n.t(
                      "label.appointmenttype",
                    )}
                  </span>

                  <span className="resource-details-value">
                    {getCodeableConceptValue(
                      appointment.appointmentType,
                    )}
                  </span>
                </div>
              </div>
            </div>
          </section>

          <section className="resource-details-section">
            <Title
              level={2}
              content={i18n.t(
                "details.sections.reasons",
              )}
            />

            <div className="row g-3">
              <div className="col-md-6">
                <div className="resource-details-card">
                  <span className="resource-details-label">
                    {i18n.t(
                      "label.cancelationreason",
                    )}
                  </span>

                  <span className="resource-details-value">
                    {getCodeableConceptValue(
                      appointment.cancelationReason,
                    )}
                  </span>
                </div>
              </div>

              <div className="col-md-6">
                <div className="resource-details-card">
                  <span className="resource-details-label">
                    {i18n.t("label.reasoncodes")}
                  </span>

                  {renderStringList(
                    getCodeableConceptValues(
                      appointment.reasonCode,
                    ),
                  )}
                </div>
              </div>

              <div className="col-12">
                <div className="resource-details-card">
                  <span className="resource-details-label">
                    {i18n.t(
                      "label.reasonreferences",
                    )}
                  </span>

                  {renderReferences(
                    appointment.reasonReference,
                  )}
                </div>
              </div>
            </div>
          </section>

          <section className="resource-details-section">
            <Title
              level={2}
              content={i18n.t(
                "details.sections.participants",
              )}
            />

            <div className="row g-3">
              {appointment.participant.map(
                (participant, index) => (
                  <div
                    className="col-12"
                    key={index}
                  >
                    <div className="resource-details-card">
                      <span className="resource-details-label">
                        {i18n.t(
                          "label.participant",
                        )}{" "}
                        {index + 1}
                      </span>

                      <div className="resource-details-value">
                        <div>
                          <strong>
                            {i18n.t("label.type")}:
                          </strong>{" "}
                          {getCodeableConceptValues(
                            participant.type,
                          ).join(", ")}
                        </div>

                        <div>
                          <strong>
                            {i18n.t("label.actor")}:
                          </strong>{" "}
                          {getReferenceValue(
                            participant.actor,
                          )}
                        </div>

                        <div>
                          <strong>
                            {i18n.t(
                              "label.required",
                            )}
                            :
                          </strong>{" "}
                          {participant.required ??
                            i18n.t(
                              "general.notavailable",
                            )}
                        </div>

                        <div>
                          <strong>
                            {i18n.t("label.status")}:
                          </strong>{" "}
                          {participant.status}
                        </div>

                        <div>
                          <strong>
                            {i18n.t("label.period")}:
                          </strong>{" "}
                          {formatPeriod(
                            participant.period?.start,
                            participant.period?.end,
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ),
              )}
            </div>
          </section>

          <section className="resource-details-section">
            <Title
              level={2}
              content={i18n.t(
                "details.sections.references",
              )}
            />

            <div className="row g-3">
              <div className="col-md-6">
                <div className="resource-details-card">
                  <span className="resource-details-label">
                    {i18n.t(
                      "label.supportinginformation",
                    )}
                  </span>

                  {renderReferences(
                    appointment.supportingInformation,
                  )}
                </div>
              </div>

              <div className="col-md-6">
                <div className="resource-details-card">
                  <span className="resource-details-label">
                    {i18n.t("label.slots")}
                  </span>

                  {renderReferences(appointment.slot)}
                </div>
              </div>

              <div className="col-md-6">
                <div className="resource-details-card">
                  <span className="resource-details-label">
                    {i18n.t("label.basedon")}
                  </span>

                  {renderReferences(appointment.basedOn)}
                </div>
              </div>

              <div className="col-md-6">
                <div className="resource-details-card">
                  <span className="resource-details-label">
                    {i18n.t(
                      "label.requestedperiods",
                    )}
                  </span>

                  {appointment.requestedPeriod?.length
                    ? renderStringList(
                        appointment.requestedPeriod.map(
                          (period) =>
                            formatPeriod(
                              period.start,
                              period.end,
                            ),
                        ),
                      )
                    : i18n.t(
                        "general.notavailable",
                      )}
                </div>
              </div>
            </div>
          </section>
        </div>
      )}
    </AcesoPage>
  );
};

export default AppointmentDetailsPage;
